import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Observable, of, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import {
  SALES_INVENTORY_SUMMARY_TTL,
  SalesInventoryStateModel,
  salesInventorySummaryKey,
} from 'ng2-app/models/sales-inventory/sales-inventory-state.model';
import { SalesInventorySummary } from 'ng2-app/models/sales-inventory/sales-inventory.model';
import { SalesInventoryService } from 'ng2-app/sales-inventory/sales-inventory.service';
import { LoadSalesInventorySummary } from 'ng2-app/store/sales-inventory/sales-inventory.actions';

@State<SalesInventoryStateModel>({
  name: 'salesInventory',
  defaults: {
    summaries: {},
    currentKey: null,
    loading: false,
    fetching: false,
    lastSync: {},
  },
})
@Injectable()
export class SalesInventoryState {
  @Selector()
  static loading(state: SalesInventoryStateModel) {
    return state?.loading ?? null;
  }

  @Selector()
  static fetching(state: SalesInventoryStateModel) {
    return state?.fetching ?? null;
  }

  /** Summary for the scope the UI is currently viewing (null until loaded). */
  @Selector()
  static summary(state: SalesInventoryStateModel) {
    const key = state?.currentKey;
    return key ? state.summaries[key] ?? null : null;
  }

  constructor(private _salesInventoryService: SalesInventoryService) {}

  private isCacheValid(
    state: SalesInventoryStateModel,
    key: string,
    force: boolean
  ): boolean {
    if (force) {
      return false;
    }
    const cache = state.lastSync[key];
    if (!cache || !(cache instanceof Date)) {
      return false;
    }
    return cache.getTime() + SALES_INVENTORY_SUMMARY_TTL > Date.now();
  }

  @Action(LoadSalesInventorySummary)
  loadSummary(
    ctx: StateContext<SalesInventoryStateModel>,
    { view, teamId, overrideCache = false }: LoadSalesInventorySummary
  ): Observable<SalesInventorySummary> {
    const key = salesInventorySummaryKey(view, teamId);
    const state = ctx.getState();

    // Cache is per-scope: only serve cached data for this exact (view, teamId). Also clears
    // loading/fetching (not just currentKey): a DIFFERENT, still-in-flight request may have
    // left them true, and its eventual response's isStillActive guard below (this key is no
    // longer active) will skip resetting them — the flags would otherwise stay stuck true.
    if (this.isCacheValid(state, key, overrideCache)) {
      ctx.patchState({ currentKey: key, loading: false, fetching: false });
      return of(state.summaries[key]);
    }

    const hasData = !!state.summaries[key];
    ctx.patchState({ currentKey: key, fetching: true, loading: !hasData });

    return this._salesInventoryService.getSummary(view, teamId).pipe(
      tap((summary) => {
        const current = ctx.getState();
        // A later selection change may have already re-keyed currentKey (and started its
        // own fetch) while this request was in flight — cache this response for later reuse,
        // but only move currentKey/loading/fetching onto it if it's still the active scope,
        // so a slow, superseded response can't clobber a newer selection's view.
        const isStillActive = current.currentKey === key;
        ctx.patchState({
          summaries: { ...current.summaries, [key]: summary },
          lastSync: { ...current.lastSync, [key]: new Date() },
          ...(isStillActive ? { currentKey: key, loading: false, fetching: false } : {}),
        });
      }),
      catchError((error) => {
        if (ctx.getState().currentKey === key) {
          ctx.patchState({ loading: false, fetching: false });
        }
        return throwError(() => error);
      })
    );
  }
}
